import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <div className={cn('mx-auto w-full px-4 py-6 sm:px-8 sm:py-8', wide ? 'max-w-[1400px]' : 'max-w-6xl', className)}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  meta,
  banner,
  actionsOnTop,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
  meta?: ReactNode;
  /** `actionsOnTop` only: a full-width block between the title and the description. */
  banner?: ReactNode;
  /**
   * The actions sit in a row above the title (opposite the eyebrow), so a long title keeps the full width on narrow
   * windows. For detail pages, whose titles are names, and for headers with many actions.
   */
  actionsOnTop?: boolean;
}) {
  if (actionsOnTop) {
    return (
      <div className="mb-6">
        {(eyebrow || actions) && (
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            {eyebrow && <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">{eyebrow}</div>}
            {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{actions}</div>}
          </div>
        )}
        <h1 className="text-xl font-semibold tracking-tight break-words sm:text-2xl">{title}</h1>
        {banner && <div className="mt-4">{banner}</div>}
        {description && <p className={cn('max-w-3xl text-sm text-muted-foreground', banner ? 'mt-3' : 'mt-1.5')}>{description}</p>}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">{meta}</div>}
      </div>
    );
  }
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        {eyebrow && <div className="mb-1.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">{eyebrow}</div>}
        <h1 className="text-xl font-semibold tracking-tight break-words sm:text-2xl">{title}</h1>
        {description && <p className="mt-1.5 max-w-3xl text-sm text-muted-foreground">{description}</p>}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Small "label: value" pair used in header meta rows and side panels. */
export function MetaItem({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-foreground">{children}</span>
    </div>
  );
}

export function DefinitionList({ items, className }: { items: { label: ReactNode; value: ReactNode }[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-2.5 text-sm', className)}>
      {items.map((it, i) => (
        <div key={i} className="contents">
          <dt className="text-muted-foreground">{it.label}</dt>
          <dd className="min-w-0 break-words">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
